import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { OrbitControls, Sky, Environment, Lightformer } from "@react-three/drei";
import { Vector3, type Group } from "three";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { useJourneyStore, selectCurrentStage } from "../../state/journeyMachine";
import { Building } from "./Building";
import { Exterior } from "./Exterior";
import { SedeHotspots } from "./sedeHotspots";
import { Avatar } from "../../entities/Avatar";
import { Person } from "../../entities/Person";
import { Vehicle } from "../../entities/Vehicle";
import { WAYPOINTS, waypointKeyForStage } from "./waypoints";
import { PREOP, MOTO_TRACK, CAR_TRACK } from "./layout";

/**
 * SedeScene — Sede de Referencia Envigado (anteproyecto arquitectónico 3D).
 *
 * Escena única: edificio + exteriores montados una sola vez; las "escenas"
 * del recorrido son waypoints de cámara + realces sobre esta escena.
 *
 * PRESUPUESTO DE RENDIMIENTO (objetivo, ver docs/PRESUPUESTO_RENDIMIENTO.md):
 *  - < 350k triángulos visibles en la vista más pesada (pista-auto aérea).
 *  - < 300 draw calls: conos instanciados, mobiliario por slots, muros
 *    extruidos como geometría única por fachada.
 *  - Sombras PCFSoft 2048 (alta) / 1024 (media) / off (baja) solo en el
 *    directional principal y objetos grandes.
 *  - frameloop="demand" cuando el recorrido está en pausa (StageRenderer).
 */
export function SedeScene() {
  const quality = useJourneyStore((s) => s.quality);
  const reducedMotion = useJourneyStore((s) => s.reducedMotion);
  const stage = useJourneyStore(selectCurrentStage);
  const waypoint = WAYPOINTS[waypointKeyForStage(stage.id)];

  const shadowSize = quality === "alta" ? 2048 : 1024;
  const sunPosition: [number, number, number] = [40, 55, 25];

  return (
    <>
      {/* iluminación global + cielo coherente con el sol */}
      <hemisphereLight args={["#dcebf5", "#66665c", 0.45]} />
      {/* refuerzo ambiente suave: levanta interiores sin lavar el exterior */}
      <ambientLight args={["#f2ede2", 0.45]} />
      <directionalLight
        position={sunPosition}
        intensity={2.4}
        color="#ffe8c4"
        castShadow={quality !== "baja"}
        shadow-mapSize={[shadowSize, shadowSize]}
        shadow-camera-left={-70}
        shadow-camera-right={70}
        shadow-camera-top={70}
        shadow-camera-bottom={-70}
        shadow-camera-far={180}
        shadow-bias={-0.0004}
      />
      <Sky sunPosition={sunPosition} turbidity={4.5} rayleigh={1.6} distance={450000} />
      <fog attach="fog" args={["#dfe8ea", 110, 320]} />
      {/* IBL procedural (sin descargas): da reflejos creíbles a vidrio,
          carrocería y metales; la intensidad se modula por material */}
      <Environment resolution={256} frames={1}>
        <color attach="background" args={["#87a8c0"]} />
        <Lightformer form="rect" intensity={3} position={[0, 12, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[24, 24, 1]} color="#fff4e0" />
        <Lightformer form="rect" intensity={1.1} position={[18, 4, 10]} rotation={[0, -Math.PI / 3, 0]} scale={[10, 5, 1]} color="#dfeaf2" />
        <Lightformer form="rect" intensity={0.9} position={[-18, 4, -8]} rotation={[0, Math.PI / 2.4, 0]} scale={[10, 5, 1]} color="#cfdde8" />
      </Environment>

      <Building />
      <Exterior />
      <SedeHotspots />

      {/* entidades */}
      <Avatar waypoint={waypoint} />
      {/* personas de ambiente (datos de demostración, sin identidad real):
          funcionaria de recepción, aspirante en sala de espera, supervisor
          de la sala teórica y evaluador en la zona preoperacional */}
      <Person pose="idle" position={[-15, 0, -7.4]} yaw={Math.PI} name="funcionario-recepcion" />
      <Person pose="sit" position={[-6.5, 0, -8]} yaw={Math.PI} name="aspirante-espera" />
      <Person pose="observe" position={[-12, 0, -24.5]} yaw={0.4} name="supervisor-teorica" />
      <Person pose="observe" position={[25.6, 0, -12.6]} yaw={2.4} name="evaluador-preop" />
      {/* fila de aspirantes frente al mostrador de recepción */}
      <Person pose="idle" position={[-16.8, 0, -5.6]} yaw={Math.PI} name="fila-1" />
      <Person pose="idle" position={[-13.6, 0, -5.2]} yaw={Math.PI} name="fila-2" />
      {/* instructor junto al inicio de la pista de automóvil */}
      <Person pose="observe" position={[-14, 0, 25]} yaw={1.2} name="instructor-pista" />
      {/* peatón en el andén público, frente a la entrada */}
      <Person pose="idle" position={[-18, 0, 40]} yaw={-0.6} name="peaton-anden" />
      <Vehicle kind="car" position={[PREOP.x, 0, PREOP.z]} rotationY={Math.PI / 2} />
      <Vehicle kind="moto" position={[MOTO_TRACK.x - MOTO_TRACK.w / 2 + 4, 0, MOTO_TRACK.z]} rotationY={Math.PI / 2} />
      <TrackCar />
      {/* auto escolta del evaluador: solo en la etapa de vía pública
          [NORMATIVO Anexo 60 num. 2.5.5.2: el evaluador dirige desde un
          automóvil de acompañamiento para A1/A2; para B/C el instructor
          va al doble mando del propio vehículo] */}
      {stage.id === "via-publica" && <EscortCar />}

      {/* anillo de realce del grupo destacado en la etapa activa */}
      <HighlightRing position={waypoint.highlight} radius={waypoint.highlightRadius} />

      <CameraRig waypoint={waypoint} reducedMotion={reducedMotion} />
    </>
  );
}

/** Anillo sobrio en el suelo que marca la zona protagonista de la etapa. */
function HighlightRing({ position, radius }: { position: [number, number, number]; radius: number }) {
  const ref = useRef<Group>(null);
  const reducedMotion = useJourneyStore((s) => s.reducedMotion);
  useFrame(({ clock }) => {
    if (ref.current && !reducedMotion) {
      const s = 1 + 0.03 * Math.sin(clock.elapsedTime * 2);
      ref.current.scale.set(s, 1, s);
    }
  });
  return (
    <group ref={ref} position={position}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[radius - 0.18, radius, 48]} />
        <meshBasicMaterial color="#1f7a7c" transparent opacity={0.75} toneMapped={false} />
      </mesh>
    </group>
  );
}

/** Automóvil de la pista, movido por la trayectoria controlada (vehicleSlice). */
function TrackCar() {
  return <Vehicle kind="car" track position={[CAR_TRACK.x - CAR_TRACK.w / 2 + 3, 0, CAR_TRACK.z]} />;
}

/** Auto de acompañamiento del evaluador: misma trayectoria, con rezago. */
function EscortCar() {
  return <Vehicle kind="car" track progressOffset={0.07} position={[CAR_TRACK.x - CAR_TRACK.w / 2 + 3, 0, CAR_TRACK.z]} />;
}

/**
 * Transiciones de cámara: lerp con damping (~1.8 s) hacia el waypoint
 * de la etapa; con reducedMotion el corte es directo.
 */
function CameraRig({
  waypoint,
  reducedMotion,
}: {
  waypoint: (typeof WAYPOINTS)[keyof typeof WAYPOINTS];
  reducedMotion: boolean;
}) {
  const controlsRef = useRef<OrbitControlsImpl>(null);
  const goalPos = useMemo(() => new Vector3(...waypoint.camera), [waypoint]);
  const goalTarget = useMemo(() => new Vector3(...waypoint.target), [waypoint]);
  const animating = useRef(false);
  const lastKey = useRef("");

  useFrame(({ camera }, delta) => {
    const controls = controlsRef.current;
    if (!controls) return;
    const key = goalPos.toArray().join(",") + goalTarget.toArray().join(",");
    if (key !== lastKey.current) {
      lastKey.current = key;
      animating.current = true;
      if (reducedMotion) {
        camera.position.copy(goalPos);
        controls.target.copy(goalTarget);
        controls.update();
        animating.current = false;
      }
    }
    if (!animating.current) return;
    // damping ~1.5–2.5 s hasta convergencia
    const k = 1 - Math.exp(-2.2 * delta);
    camera.position.lerp(goalPos, k);
    controls.target.lerp(goalTarget, k);
    controls.update();
    if (camera.position.distanceTo(goalPos) < 0.03 && controls.target.distanceTo(goalTarget) < 0.03) {
      animating.current = false;
    }
  });

  return (
    <OrbitControls
      ref={controlsRef}
      makeDefault
      enableDamping={!reducedMotion}
      dampingFactor={0.08}
      maxPolarAngle={Math.PI / 2.05}
      minDistance={2}
      maxDistance={110}
    />
  );
}
